import {
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams,
  GridActionsColDef
} from '@mui/x-data-grid-premium';
import { baseCell, currencyCell } from 'components/cells';
import listCell from 'components/cells/list-cell';
import {
  booleanFilterOperators,
  filterOperators,
  numberFilterOperators,
  singleSelectFilterOperators
} from 'components/enhanced';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { useTranslation } from 'react-i18next';
import { useIsBottomFromTenantAdmin } from 'shared/permissions';
import { StatusOption, OptionDescription } from 'shared/types';
import {
  AssociationCell,
  DefaultLocationAction,
  EditThresholdAction,
  ManagePricesAction,
  SourceCostAction
} from './thresholds-utils';

export function ThresholdsColumns(
  disableEdit: boolean,
  onManagePrices?: (product: string) => void,
  refresh?: any,
  statusOptions: StatusOption[] = [],
  categoryOptions: OptionDescription[] = []
): GridColDef[] {
  const { t } = useTranslation();

  const columns: GridColDef[] = [];

  if (useIsBottomFromTenantAdmin()) {
    columns.push({
      field: 'association',
      headerName: t(`global.association`),
      width: 80,
      filterOperators: booleanFilterOperators,
      renderCell: (params: GridRenderCellParams) => <AssociationCell row={params.row} />
    });
  }

  columns.push(
    {
      field: 'product',
      headerName: t(`global.product`),
      filterOperators,
      width: 150,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      filterOperators,
      width: 400,
      renderCell: baseCell
    },
    {
      field: 'status',
      headerName: t(`entities.product.status`),
      width: 100,
      type: 'singleSelect',
      editable: false,
      filterOperators: singleSelectFilterOperators,
      valueOptions: statusOptions.map((opt) => ({
        value: String(opt.value),
        label: t(`${opt.display_name}`)
      })),
      renderCell: ({ value }) => {
        return (
          <span className={value === 'ACTIVE' ? 'text-green-500' : 'text-orange-500'}>
            {t(`entities.product.status_options.${value}`)}
          </span>
        );
      }
    },
    {
      field: 'category',
      headerName: t(`entities.product.category`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      filterOperators,
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'location',
      headerName: t(`global.locations`),
      filterOperators,
      filterable: false,
      width: 250,
      renderCell: listCell
    },
    {
      field: 'default_location',
      headerName: t(`global.default-location`),
      filterOperators,
      width: 160,
      valueGetter: (params) => params.row?.default_location || '',
      renderCell: baseCell
    },
    {
      field: 'contract_code',
      headerName: t(`global.contract`),
      width: 130,
      valueGetter: (params) => params.row?.contract_code || '',
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 130,
      valueGetter: (params) => params.row?.supplier || '',
      renderCell: baseCell
    },
    {
      field: 'unit_price',
      headerName: t(`entities.contract_line.unit_price`),
      width: 130,
      valueGetter: (params) => params.row?.unit_price ?? '',
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    },
    {
      field: 'initial_available_units',
      headerName: t(`entities.contract_line.initial_available_units`),
      width: 130,
      valueGetter: (params) => params.row?.initial_available_units ?? '',
      renderCell: baseCell
    },
    {
      field: 'available_units',
      headerName: t(`entities.contract_line.available_units`),
      width: 130,
      valueGetter: (params) => params.row?.available_units ?? '',
      renderCell: baseCell
    },
    {
      field: 'initial_discounted_units',
      headerName: t(`entities.contract_line.initial_discounted_units`),
      width: 150,
      valueGetter: (params) => params.row?.initial_discounted_units ?? '',
      renderCell: baseCell
    },
    {
      field: 'available_discounted_units',
      headerName: t(`entities.contract_line.available_discounted_units`),
      width: 150,
      valueGetter: (params) => params.row?.initial_discounted_units ?? '',
      renderCell: baseCell
    },
    {
      field: 'total_stock_quantity',
      headerName: t(`global.stock`),
      width: 130,
      type: 'number',
      filterOperators: numberFilterOperators,
      valueGetter: (params) => params.row?.total_stock_quantity ?? 0,
      renderCell: baseCell
    },
    {
      field: 'notes',
      headerName: t(`global.notes`),
      filterOperators,
      width: 350,
      renderCell: baseCell
    },
    {
      field: 'label',
      width: 100,
      valueGetter: (params) => params.row?.label,
      headerName: t(`global.label`)
    }
  );

  ['restock_threshold', 'understock_threshold', 'overstock_threshold'].forEach((threshold) => {
    columns.push({
      field: threshold,
      headerName: t(`entities.product.${threshold}`),
      type: 'number',
      width: 100,
      filterOperators: numberFilterOperators,
      renderCell: baseCell
    });
  });

  columns.push({
    field: 'protocollo',
    headerName: t(`entities.product.protocollo`),
    filterOperators,
    width: 160,
    valueGetter: (params) => params.row?.protocollo ?? '',
    renderCell: baseCell
  });

  if (useIsBottomFromTenantAdmin() && disableEdit) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      width: 120,
      getActions: (params) => [
        <DefaultLocationAction
          key={`threshold-default-location-${params.id}`}
          row={params.row}
          t={t}
          refresh={refresh}
        />,
        <EditThresholdAction
          key={`threshold-edit-${params.id}`}
          row={params.row}
          t={t}
          refresh={refresh}
        />,
        <ManagePricesAction
          key={`threshold-manage-${params.id}`}
          row={params.row}
          t={t}
          onManagePrices={onManagePrices}
        />,
        <SourceCostAction
          key={`threshold-costs-${params.id}`}
          row={params.row}
          t={t}
          onManagePrices={onManagePrices}
        />
      ]
    } as GridActionsColDef);
  }

  return columns;
}
